/**
 * Derives "missing progress note" rows from FHIR Appointments: post-session clinical
 * visits with no QuestionnaireResponse tagged to the appointment
 * (same linkage as the Appointment page / chart).
 */

import type { MedplumClient } from '@medplum/core';
import type { Appointment, QuestionnaireResponse } from '@medplum/fhirtypes';
import { APPOINTMENT_CATEGORY_SYSTEM } from '@unify/fhir-model/constants';
import { isFhirAppointmentPostSession } from '@unify/fhir-model/resources/appointment';
import {
  getAppointmentIdFromQR,
  getSignatureFromQR,
  PROVIDER_SIGNATURE_EXT_URL,
} from '../../appointments/fhirNoteMapping';
import type { MissingDocumentItem, SupervisionQueueItemWithDocumentId } from '../types';

export { isFhirAppointmentPostSession };

/** Past sessions that still need a note, including no-show and cancel. */
export const POST_SESSION_NOTE_APPOINTMENT_STATUSES = [
  'booked',
  'arrived',
  'checked-in',
  'fulfilled',
  'noshow',
  'cancelled',
] as const;

const POST_SESSION_NOTE_STATUS_SET = new Set<string>(POST_SESSION_NOTE_APPOINTMENT_STATUSES);

function isClinicalSessionAppointment(apt: Appointment): boolean {
  const category = apt.serviceCategory?.[0]?.coding?.find(
    (c) => c.system === APPOINTMENT_CATEGORY_SYSTEM,
  )?.code;
  if (category === 'meeting' || category === 'time-entry') return false;
  const hasPatient = apt.participant?.some((p) => p.actor?.reference?.startsWith('Patient/'));
  const hasPractitioner = apt.participant?.some((p) =>
    p.actor?.reference?.startsWith('Practitioner/'),
  );
  return Boolean(hasPatient && hasPractitioner);
}

export function isEligibleMissingNoteAppointment(apt: Appointment, nowMs = Date.now()): boolean {
  if (!apt.id) return false;
  if (!apt.status || !POST_SESSION_NOTE_STATUS_SET.has(apt.status)) return false;
  if (!isFhirAppointmentPostSession(apt, nowMs)) return false;
  return isClinicalSessionAppointment(apt);
}

function firstPatientFromAppointment(apt: Appointment): { id: string; display: string } | null {
  for (const p of apt.participant ?? []) {
    const ref = p.actor?.reference;
    if (ref?.startsWith('Patient/')) {
      const id = ref.replace('Patient/', '');
      return { id, display: p.actor?.display ?? '—' };
    }
  }
  return null;
}

/** Practitioner participant matching `practitionerId` (not necessarily the first practitioner). */
export function matchingPractitionerParticipant(
  apt: Appointment,
  practitionerId: string,
): { id: string; display: string } | null {
  for (const p of apt.participant ?? []) {
    const ref = p.actor?.reference;
    if (!ref?.startsWith('Practitioner/')) continue;
    const id = ref.replace('Practitioner/', '');
    if (id === practitionerId) {
      return { id, display: p.actor?.display ?? '—' };
    }
  }
  return null;
}

function matchingScopedPractitioner(
  apt: Appointment,
  scopedPractitionerIds: string[],
): { id: string; display: string } | null {
  for (const id of scopedPractitionerIds) {
    const match = matchingPractitionerParticipant(apt, id);
    if (match) return match;
  }
  return null;
}

function qrCountsAsPresentNote(qr: QuestionnaireResponse, requireSigned: boolean): boolean {
  const appointmentId = getAppointmentIdFromQR(qr);
  if (!appointmentId) return false;
  if (!requireSigned) return true;
  return Boolean(getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL));
}

async function loadAppointmentIdsWithNotesForPatients(
  medplum: MedplumClient,
  patientIds: string[],
  requireSigned: boolean,
): Promise<Set<string>> {
  const ids = new Set<string>();
  await Promise.all(
    patientIds.map(async (pid) => {
      try {
        const qrs = await medplum.searchResources('QuestionnaireResponse', {
          subject: `Patient/${pid}`,
          _count: '500',
        });
        for (const qr of qrs) {
          if (!qrCountsAsPresentNote(qr, requireSigned)) continue;
          const aid = getAppointmentIdFromQR(qr);
          if (aid) ids.add(aid);
        }
      } catch {
        /* ignore per-patient failures */
      }
    }),
  );
  return ids;
}

export interface FetchMissingProgressNotesOpts {
  /**
   * When true (Pending Documents), a draft / unsigned QR does not hide the session.
   * Unsigned Notes keeps the default (false) so a draft row is not duplicated as "missing".
   */
  treatUnsignedAsMissing?: boolean;
}

/**
 * Fulfilled / past booked clinical sessions for the given practitioners that have no
 * progress note (QR) tagged with the appointment id.
 */
export async function fetchMissingProgressNotesFromAppointments(
  medplum: MedplumClient,
  scopedPractitionerIds: string[],
  opts?: FetchMissingProgressNotesOpts,
): Promise<MissingDocumentItem[]> {
  if (scopedPractitionerIds.length === 0) return [];

  const nowIso = new Date().toISOString();
  const requireSigned = opts?.treatUnsignedAsMissing === true;
  const byAppointmentId = new Map<string, Appointment>();

  const consider = (apt: Appointment): void => {
    if (!apt.id || !isEligibleMissingNoteAppointment(apt)) return;
    if (!matchingScopedPractitioner(apt, scopedPractitionerIds)) return;
    if (!firstPatientFromAppointment(apt)) return;
    byAppointmentId.set(apt.id, apt);
  };

  await Promise.all(
    scopedPractitionerIds.map(async (practitionerId) => {
      try {
        const appts = await medplum.searchResources('Appointment', {
          actor: `Practitioner/${practitionerId}`,
          date: `lt${nowIso}`,
          status: POST_SESSION_NOTE_APPOINTMENT_STATUSES.join(','),
          _sort: '-date',
          _count: '100',
        });
        for (const a of appts) consider(a);
      } catch {
        /* ignore per-practitioner failures */
      }
    }),
  );

  try {
    const compartmentAppts = await medplum.searchResources('Appointment', {
      date: `lt${nowIso}`,
      status: POST_SESSION_NOTE_APPOINTMENT_STATUSES.join(','),
      _sort: '-date',
      _count: '200',
    });
    for (const a of compartmentAppts) consider(a);
  } catch {
    /* ignore — actor searches above remain the primary source */
  }

  const candidates = [...byAppointmentId.values()];
  const uniquePatients = [...new Set(candidates.map((a) => firstPatientFromAppointment(a)?.id).filter(Boolean))] as string[];

  const appointmentIdsWithNotes = await loadAppointmentIdsWithNotesForPatients(
    medplum,
    uniquePatients,
    requireSigned,
  );

  const items: MissingDocumentItem[] = [];
  const seen = new Set<string>();

  for (const a of candidates) {
    if (!a.id || appointmentIdsWithNotes.has(a.id)) continue;
    const prac = matchingScopedPractitioner(a, scopedPractitionerIds);
    const patient = firstPatientFromAppointment(a);
    if (!prac || !patient) continue;

    const key = `${prac.id}|${patient.id}|${(a.start ?? '').slice(0, 10)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const sessionDay = a.start?.slice(0, 10) ?? '';
    const dueDay = a.end?.slice(0, 10) ?? sessionDay;

    items.push({
      id: `appt-missing-note-${a.id}`,
      category: 'missing-notes',
      clinicianId: prac.id,
      clinicianName: prac.display,
      clientId: patient.id,
      clientName: patient.display,
      appointmentId: a.id,
      sessionDate: sessionDay,
      dueDate: dueDay,
      /** Tab shows missing-notes when overdue; due-soon is hidden for this category. */
      status: 'overdue',
      description: `Progress note for session on ${sessionDay}`,
    });
  }

  return items;
}

/**
 * Avoid duplicate rows if a supervision Task already represents the same session (same provider, client, session day).
 */
export function filterAppointmentItemsDedupedAgainstTasks(
  apptItems: MissingDocumentItem[],
  taskItems: MissingDocumentItem[],
): MissingDocumentItem[] {
  return apptItems.filter((a) => {
    if (a.category !== 'missing-notes') return true;
    const clash = taskItems.some(
      (t) =>
        t.category === 'missing-notes' &&
        t.clinicianId === a.clinicianId &&
        t.clientId === a.clientId &&
        (t.sessionDate ?? '') === (a.sessionDate ?? ''),
    );
    return !clash;
  });
}

/** Map a missing session-note row onto the Unsigned Notes queue (no QR yet). */
export function missingProgressNoteToQueueItem(
  item: MissingDocumentItem,
): SupervisionQueueItemWithDocumentId {
  const sessionDay = item.sessionDate ?? item.dueDate ?? '';
  return {
    id: item.id,
    documentType: 'notes',
    title: item.description ?? (sessionDay ? `Progress note – ${item.clientName ?? 'Client'}` : 'Progress note'),
    clinicianId: item.clinicianId,
    clinicianName: item.clinicianName,
    clientId: item.clientId,
    clientName: item.clientName,
    status: 'draft',
    dueDate: item.dueDate,
    submittedAt: item.sessionDate,
    isOverdue: item.status === 'overdue',
    appointmentId: item.appointmentId,
  };
}
